import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Flex,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import {
  AlertCircle,
  Check,
  Database,
  Filter,
  Minus,
  Plus,
  SquareMinus,
  SquarePen,
  SquarePlus,
  Trash,
  Tv,
  X,
} from 'lucide-react';
import useChannelsStore from '../../store/channels';
import useWarningsStore from '../../store/warnings';
import ConfirmationDialog from '../ConfirmationDialog';
import { showNotification } from '../../utils/notificationUtils.js';
import {
  addChannelGroup,
  claimChannelGroupAsCustom,
  cleanupUnusedChannelGroups,
  deleteChannelGroup,
  unclaimChannelGroup,
  updateChannelGroup,
} from '../../utils/forms/ChannelGroupUtils.js';

const toGroupUsage = (group) => ({
  hasChannels: group.channel_count > 0,
  hasM3UAccounts: group.m3u_account_count > 0,
  canEdit: group.m3u_account_count === 0 || group.is_custom,
  canDelete: group.channel_count === 0 && group.m3u_account_count === 0,
});

// Move GroupItem outside to prevent recreation on every render
const GroupItem = React.memo(
  ({
    group,
    editingGroupId,
    editName,
    onEditNameChange,
    onSaveEdit,
    onCancelEdit,
    onEdit,
    onDelete,
    onDemote,
    onClaimCustom,
    groupUsage,
    canEditGroup,
    canDeleteGroup,
  }) => {
    const theme = useMantineTheme();
    const usage = groupUsage[group.id];

    const getGroupBadges = (group) => {
      const badges = [];

      if (usage?.hasChannels) {
        badges.push(
          <Badge
            key="channels"
            size="xs"
            color="blue"
            leftSection={<Tv size={10} />}
          >
            Channels
          </Badge>
        );
      }

      if (usage?.hasM3UAccounts) {
        badges.push(
          <Badge
            key="m3u"
            size="xs"
            color="purple"
            leftSection={<Database size={10} />}
          >
            M3U
          </Badge>
        );
      }

      if (!usage?.hasChannels && !usage?.hasM3UAccounts) {
        badges.push(
          <Badge key="unused" size="xs" color="gray">
            Unused
          </Badge>
        );
      }

      if (group.is_custom) {
        badges.push(
          <Badge key="custom" size="xs" color="dark">
            Custom
          </Badge>
        );
      }

      return badges;
    };

    return (
      <Box
        style={{
          border: '1px solid #444',
          borderRadius: '8px',
          backgroundColor:
            editingGroupId === group.id
              ? '#3f3f46'
              : group.enabled
                ? '#2A2A2E'
                : '#1E1E22',
          padding: '8px',
          marginBottom: '2px',
        }}
      >
        <Group justify="space-between" gap="xs" align="flex-start">
          <Stack gap={4} style={{ flex: 1 }}>
            {editingGroupId === group.id ? (
              <TextInput
                value={editName}
                onChange={onEditNameChange}
                size="sm"
                onKeyPress={(e) => e.key === 'Enter' && onSaveEdit()}
                autoFocus
              />
            ) : (
              <>
                <Text size="sm" fw={500}>
                  {group.name}
                </Text>
                <Group gap={4}>{getGroupBadges(group)}</Group>
              </>
            )}
          </Stack>

          <Group gap="xs">
            {editingGroupId === group.id ? (
              <>
                <ActionIcon color="green" size="sm" onClick={onSaveEdit}>
                  <Check size={14} />
                </ActionIcon>
                <ActionIcon color="gray" size="sm" onClick={onCancelEdit}>
                  <X size={14} />
                </ActionIcon>
              </>
            ) : (
              <>
                <ActionIcon
                  variant="transparent"
                  color={
                    group.is_custom
                      ? theme.tailwind.orange[4]
                      : theme.tailwind.green[4]
                  }
                  size="sm"
                  title={
                    group.is_custom ? 'Demote from Custom' : 'Upgrade to Custom'
                  }
                  aria-label={
                    group.is_custom ? 'Demote from Custom' : 'Upgrade to Custom'
                  }
                  onClick={() =>
                    group.is_custom ? onDemote(group) : onClaimCustom(group)
                  }
                  disabled={
                    group.is_custom &&
                    (usage?.hasChannels || !usage?.hasM3UAccounts)
                  }
                >
                  {group.is_custom ? (
                    <Minus size={18} strokeWidth={3} />
                  ) : (
                    <Plus size={18} strokeWidth={3} />
                  )}
                </ActionIcon>
                <ActionIcon
                  variant="transparent"
                  color={theme.tailwind.yellow[3]}
                  size="sm"
                  onClick={() => onEdit(group)}
                  disabled={!canEditGroup(group)}
                >
                  <SquarePen size={18} />
                </ActionIcon>
                <ActionIcon
                  variant="transparent"
                  color={theme.tailwind.red[6]}
                  size="sm"
                  title="Delete group"
                  aria-label="Delete group"
                  onClick={() => onDelete(group)}
                  disabled={!canDeleteGroup(group)}
                >
                  <SquareMinus size="18" />
                </ActionIcon>
              </>
            )}
          </Group>
        </Group>
      </Box>
    );
  }
);

const GroupManager = React.memo(({ isOpen, onClose }) => {
  const channelGroups = useChannelsStore((s) => s.channelGroups);
  const canEditChannelGroup = useChannelsStore((s) => s.canEditChannelGroup);
  const canDeleteChannelGroup = useChannelsStore(
    (s) => s.canDeleteChannelGroup
  );
  const isWarningSuppressed = useWarningsStore((s) => s.isWarningSuppressed);
  const suppressWarning = useWarningsStore((s) => s.suppressWarning);

  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [groupUsage, setGroupUsage] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [showChannelGroups, setShowChannelGroups] = useState(true);
  const [showM3UGroups, setShowM3UGroups] = useState(true);
  const [showUnusedGroups, setShowUnusedGroups] = useState(true);

  // Confirmation dialog states
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupAction, setGroupAction] = useState(null);
  const [confirmCleanupOpen, setConfirmCleanupOpen] = useState(false);
  const [includeCustomInCleanup, setIncludeCustomInCleanup] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);

  // Memoize the channel groups array to prevent unnecessary re-renders
  const channelGroupsArray = useMemo(
    () => Object.values(channelGroups),
    [channelGroups]
  );

  // Memoize sorted groups to prevent re-sorting on every render
  const sortedGroups = useMemo(
    () => channelGroupsArray.sort((a, b) => a.name.localeCompare(b.name)),
    [channelGroupsArray]
  );

  const { filteredGroups, filterCounts } = useMemo(() => {
    const counts = { channels: 0, m3u: 0, unused: 0 };
    const filtered = [];

    for (const group of sortedGroups) {
      const usage = groupUsage[group.id];
      if (!usage) continue;

      const { hasChannels, hasM3UAccounts: hasM3U } = usage;
      const isUnused = !hasChannels && !hasM3U;

      if (hasChannels) counts.channels++;
      if (hasM3U) counts.m3u++;
      if (isUnused) counts.unused++;

      const matchesSearch =
        !searchTerm.trim() ||
        group.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = isUnused
        ? showUnusedGroups
        : (hasChannels && showChannelGroups) || (hasM3U && showM3UGroups);

      if (matchesSearch && matchesFilter) {
        filtered.push(group);
      }
    }

    return { filteredGroups: filtered, filterCounts: counts };
  }, [
    sortedGroups,
    groupUsage,
    searchTerm,
    showChannelGroups,
    showM3UGroups,
    showUnusedGroups,
  ]);

  const fetchGroupUsage = useCallback(async () => {
    setLoading(true);
    try {
      // Use the actual channel group data that already has the flags
      const usage = {};

      Object.values(channelGroups).forEach((group) => {
        usage[group.id] = {
          hasChannels: group.hasChannels ?? false,
          hasM3UAccounts: group.hasM3UAccounts ?? false,
          canEdit: group.canEdit ?? true,
          canDelete: group.canDelete ?? true,
        };
      });

      setGroupUsage(usage);
    } catch (error) {
      console.error('Error fetching group usage:', error);
    } finally {
      setLoading(false);
    }
  }, [channelGroups]);

  // Fetch group usage information when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchGroupUsage();
    }
  }, [isOpen, fetchGroupUsage]);

  const handleEdit = useCallback((group) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editName.trim()) {
      showNotification({
        title: 'Error',
        message: 'Group name cannot be empty',
        color: 'red',
      });
      return;
    }

    try {
      const updatedGroup = await updateChannelGroup(channelGroups[editingGroupId], {
        name: editName.trim(),
      });

      if (updatedGroup?.demoted_group) {
        setGroupUsage((usage) => ({
          ...usage,
          [updatedGroup.id]: toGroupUsage(updatedGroup),
          [updatedGroup.demoted_group.id]: toGroupUsage(
            updatedGroup.demoted_group
          ),
        }));
      }

      showNotification({
        title: 'Success',
        message: 'Group updated successfully',
        color: 'green',
      });

      setEditingGroupId(null);
      setEditName('');
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to update group',
        color: 'red',
      });
    }
  }, [channelGroups, editName, editingGroupId]);

  const handleCancelEdit = useCallback(() => {
    setEditingGroupId(null);
    setEditName('');
  }, []);

  const handleCreate = useCallback(async () => {
    if (!newGroupName.trim()) {
      showNotification({
        title: 'Error',
        message: 'Group name cannot be empty',
        color: 'red',
      });
      return;
    }

    try {
      await addChannelGroup({
        name: newGroupName.trim(),
      });

      showNotification({
        title: 'Success',
        message: 'Group created successfully',
        color: 'green',
      });

      setNewGroupName('');
      setIsCreating(false);
      await fetchGroupUsage(); // Refresh usage data
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to create group',
        color: 'red',
      });
    }
  }, [newGroupName, fetchGroupUsage]);

  const executeDeleteGroup = useCallback(
    async (group) => {
      setDeletingGroup(true);
      try {
        await deleteChannelGroup(group);

        showNotification({
          title: 'Success',
          message: 'Group deleted successfully',
          color: 'green',
        });

        await fetchGroupUsage(); // Refresh usage data
      } catch (error) {
        showNotification({
          title: 'Error',
          message: 'Failed to delete group',
          color: 'red',
        });
      } finally {
        setDeletingGroup(false);
        setConfirmDeleteOpen(false);
      }
    },
    [fetchGroupUsage]
  );

  const handleDelete = useCallback(
    async (group) => {
      const usage = groupUsage[group.id];

      if (!usage?.canDelete) {
        showNotification({
          title: 'Cannot Delete',
          message: 'This group is associated with channels or M3U accounts and cannot be deleted',
          color: 'orange',
        });
        return;
      }

      // Store group for confirmation dialog
      setGroupToDelete(group);
      setGroupAction('delete');

      // Skip warning if it's been suppressed
      if (isWarningSuppressed('delete-group')) {
        return executeDeleteGroup(group);
      }

      setConfirmDeleteOpen(true);
    },
    [groupUsage, isWarningSuppressed, executeDeleteGroup]
  );

  const executeDemoteGroup = useCallback(
    async (group) => {
      setDeletingGroup(true);
      try {
        await unclaimChannelGroup(group);
        showNotification({
          title: 'Success',
          message: 'Group unclaimed successfully',
          color: 'green',
        });
        await fetchGroupUsage();
      } catch (error) {
        showNotification({
          title: 'Error',
          message: 'Failed to unclaim group',
          color: 'red',
        });
      } finally {
        setDeletingGroup(false);
        setConfirmDeleteOpen(false);
      }
    },
    [fetchGroupUsage]
  );

  const handleDemote = useCallback(
    (group) => {
      if (groupUsage[group.id]?.hasChannels) return;

      setGroupToDelete(group);
      setGroupAction('demote');
      if (isWarningSuppressed('delete-group')) {
        return executeDemoteGroup(group);
      }
      setConfirmDeleteOpen(true);
    },
    [executeDemoteGroup, groupUsage, isWarningSuppressed]
  );

  const handleClaimCustom = useCallback(
    async (group) => {
      try {
        await claimChannelGroupAsCustom(group);
        showNotification({
          title: 'Success',
          message: 'Group claimed as custom',
          color: 'green',
        });
      } catch {
        showNotification({
          title: 'Error',
          message: 'Failed to claim group as custom',
          color: 'red',
        });
      }
    },
    []
  );

  const executeCleanup = useCallback(async (includeCustom = false) => {
    setIsCleaningUp(true);
    try {
      const result = await cleanupUnusedChannelGroups(includeCustom);

      showNotification({
        title: 'Cleanup Complete',
        message: `Successfully deleted ${result.deleted_count} unused groups`,
        color: 'green',
      });

      await fetchGroupUsage(); // Refresh usage data
      setConfirmCleanupOpen(false);
    } catch (error) {
      showNotification({
        title: 'Cleanup Failed',
        message: 'Failed to cleanup unused groups',
        color: 'red',
      });
      setConfirmCleanupOpen(false);
    } finally {
      setIsCleaningUp(false);
    }
  }, [fetchGroupUsage]);

  const handleCleanup = useCallback(async () => {
    setIncludeCustomInCleanup(false);
    // Skip warning if it's been suppressed
    if (isWarningSuppressed('cleanup-groups')) {
      return executeCleanup(false);
    }

    setConfirmCleanupOpen(true);
  }, [isWarningSuppressed, executeCleanup]);

  const handleNewGroupNameChange = useCallback((e) => {
    setNewGroupName(e.target.value);
  }, []);

  const handleEditNameChange = useCallback((e) => {
    setEditName(e.target.value);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={onClose}
        title="Group Manager"
        size="lg"
        scrollAreaComponent={ScrollArea.Autosize}
        zIndex={2000}
      >
        <Stack>
          <Alert icon={<AlertCircle size={16} />} color="blue" variant="light">
            Manage channel groups. Promote groups to protect them from automatic
            removal, or demote empty custom groups to remove that protection.
          </Alert>

          {/* Create new group section */}
          <Group justify="space-between">
            {isCreating ? (
              <Group style={{ flex: 1 }}>
                <TextInput
                  placeholder="Enter group name"
                  value={newGroupName}
                  onChange={handleNewGroupNameChange}
                  style={{ flex: 1 }}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
                <ActionIcon color="green" onClick={handleCreate}>
                  <Check size={16} />
                </ActionIcon>
                <ActionIcon
                  color="gray"
                  onClick={() => {
                    setIsCreating(false);
                    setNewGroupName('');
                  }}
                >
                  <X size={16} />
                </ActionIcon>
              </Group>
            ) : (
              <Button
                leftSection={<SquarePlus size={16} />}
                variant="light"
                size="sm"
                onClick={() => setIsCreating(true)}
              >
                Add Group
              </Button>
            )}

            {!isCreating && (
              <Button
                leftSection={<Trash size={16} />}
                variant="light"
                size="sm"
                color="orange"
                onClick={handleCleanup}
                loading={isCleaningUp}
              >
                Cleanup Unused
              </Button>
            )}
          </Group>

          <Divider />

          {/* Filter Controls */}
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group align="center" gap="sm">
                <Filter size={16} />
                <Text size="sm" fw={600}>
                  Filter Groups
                </Text>
              </Group>
              <TextInput
                placeholder="Search groups..."
                value={searchTerm}
                onChange={handleSearchChange}
                size="sm"
                style={{ width: '200px' }}
                rightSection={
                  searchTerm && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={() => setSearchTerm('')}
                    >
                      <X size={14} />
                    </ActionIcon>
                  )
                }
              />
            </Group>

            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">
                Show:
              </Text>
              <Chip
                checked={showChannelGroups}
                onChange={setShowChannelGroups}
                size="sm"
                color="blue"
              >
                <Group gap={4}>
                  <Tv size={10} />
                  Channel Groups ({filterCounts.channels})
                </Group>
              </Chip>
              <Chip
                checked={showM3UGroups}
                onChange={setShowM3UGroups}
                size="sm"
                color="purple"
              >
                <Group gap={4}>
                  <Database size={10} />
                  M3U Groups ({filterCounts.m3u})
                </Group>
              </Chip>
              <Chip
                checked={showUnusedGroups}
                onChange={setShowUnusedGroups}
                size="sm"
                color="gray"
              >
                Unused Groups ({filterCounts.unused})
              </Chip>
            </Group>
          </Stack>

          <Divider />

          {/* Existing groups */}
          <Stack>
            <Text size="sm" fw={600}>
              Groups ({filteredGroups.length}
              {(searchTerm ||
                !showChannelGroups ||
                !showM3UGroups ||
                !showUnusedGroups) &&
                ` of ${sortedGroups.length}`}
              )
            </Text>

            {loading ? (
              <Text size="sm" c="dimmed">
                Loading group information...
              </Text>
            ) : filteredGroups.length === 0 ? (
              <Text size="sm" c="dimmed">
                {searchTerm ||
                !showChannelGroups ||
                !showM3UGroups ||
                !showUnusedGroups
                  ? 'No groups found matching your filters'
                  : 'No groups found'}
              </Text>
            ) : (
              <Stack gap="xs">
                {filteredGroups.map((group) => (
                  <GroupItem
                    key={group.id}
                    group={group}
                    editingGroupId={editingGroupId}
                    editName={editName}
                    onEditNameChange={handleEditNameChange}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onDemote={handleDemote}
                    onClaimCustom={handleClaimCustom}
                    groupUsage={groupUsage}
                    canEditGroup={canEditChannelGroup}
                    canDeleteGroup={canDeleteChannelGroup}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          <Divider />

          <Flex justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Flex>
        </Stack>
      </Modal>

      <ConfirmationDialog
        opened={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() =>
          groupToDelete &&
          (groupAction === 'demote'
            ? executeDemoteGroup(groupToDelete)
            : executeDeleteGroup(groupToDelete))
        }
        loading={deletingGroup}
        title={
          groupAction === 'demote'
            ? 'Confirm Group Demotion'
            : 'Confirm Group Deletion'
        }
        message={
          groupToDelete ? (
            <div style={{ whiteSpace: 'pre-line' }}>
              {groupAction === 'demote'
                ? `Are you sure you want to unclaim the following group?

Name: ${groupToDelete.name}

Provider-only cleanup can remove it after it is unclaimed.`
                : `Are you sure you want to delete the following group?

Name: ${groupToDelete.name}

This action cannot be undone.`}
            </div>
          ) : (
            groupAction === 'demote'
              ? 'Are you sure you want to unclaim this group?'
              : 'Are you sure you want to delete this group? This action cannot be undone.'
          )
        }
        confirmLabel={groupAction === 'demote' ? 'Demote' : 'Delete'}
        cancelLabel="Cancel"
        actionKey="delete-group"
        onSuppressChange={suppressWarning}
        size="md"
        zIndex={2100}
      />

      <ConfirmationDialog
        opened={confirmCleanupOpen}
        onClose={() => setConfirmCleanupOpen(false)}
        onConfirm={() => executeCleanup(includeCustomInCleanup)}
        loading={isCleaningUp}
        title="Confirm Group Cleanup"
        message={
          <div style={{ whiteSpace: 'pre-line' }}>
            {`Are you sure you want to cleanup unused provider-only groups?

Custom groups are kept unless you choose to include them.

This action cannot be undone.`}
            <Checkbox
              mt="sm"
              label="Also delete unused custom groups"
              checked={includeCustomInCleanup}
              onChange={(event) =>
                setIncludeCustomInCleanup(event.currentTarget.checked)
              }
            />
          </div>
        }
        confirmLabel="Cleanup"
        cancelLabel="Cancel"
        actionKey="cleanup-groups"
        onSuppressChange={suppressWarning}
        size="md"
        zIndex={2100}
      />
    </>
  );
});

export default GroupManager;
